import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck } from "lucide-react";

const featureList = [
  {
    title: "Real-Time Carbon Tracking",
    description: "Get precise AI-driven CO₂ calculations for your vehicles based on distance and load.",
    image: "/image1.webp",
  },
  {
    title: "Emission Reduction Strategies",
    description: "Access expert-backed strategies tailored to your logistics operations to help reduce emissions effectively.",
    image: "/image2.webp",
  },
  {
    title: "Sustainability Reporting",
    description: "Generate comprehensive reports that align with global sustainability standards and help you communicate your progress.",
    image: "/image3.jpg",
  },
  {
    title: "Customizable Alerts",
    description: "Set alerts for emission thresholds to stay proactive and maintain compliance with environmental regulations.",
    image: "/image4.jpg",
  },
  {
    title: "Carbon Offset Integration",
    description: "Seamlessly integrate carbon offset programs directly into your logistics workflow to neutralize emissions.",
    image: "/image5.jpg",
  },
  {
    title: "User-Friendly Interface",
    description: "Navigate an intuitive platform designed with user experience in mind to make sustainability accessible for all.",
    image: "/image6.jpg",
  },
];

const Features = () => {
  return (
    <section className="relative py-24 bg-white font-sans text-slate-900 overflow-hidden" id="features">
      {/* Background Image Overlay */}
      <img
        className="absolute top-0 left-0 w-full h-full object-cover z-0 opacity-10 pointer-events-none"
        src="/featuresbg4.avif"
        alt="Features Background"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-emerald-100/80 border border-emerald-300 px-4 py-1.5 rounded-full mb-4 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <span className="text-xs sm:text-sm font-bold text-emerald-900 tracking-wide uppercase">
              Powered by AI
            </span>
          </motion.div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Key Features: <span className="text-[#0E3B2E]">Revolutionizing Green Logistics</span>
          </h2>

          <p className="text-base sm:text-lg text-slate-600 font-medium max-w-2xl mt-3 leading-relaxed">
            Everything your fleet needs to measure emissions, streamline compliance, and issue verified green certificates.
          </p>
        </div>

        {/* 6 Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featureList.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-white border border-slate-200/90 rounded-3xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group text-center"
            >
              <div>
                {/* Feature Image Ring */}
                <div className="w-28 h-28 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#0E3B2E] p-1 shadow-md group-hover:scale-105 transition-transform">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                <h3 className="text-xl font-bold text-[#0E3B2E] mb-3 tracking-tight">
                  {feature.title}
                </h3>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
                  {feature.description}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-center gap-1.5 text-xs font-extrabold text-emerald-800 uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <span>Enterprise Grade</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
